import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import type { Team } from '@/types/app';
import { useUsers } from './useUsers';
import { useArchiveTeam, useTeams, useUpsertTeam } from './useTeams';

export default function TeamsPage() {
  const { t } = useTranslation();
  const { data: teams, isPending, isError } = useTeams();
  const { data: users } = useUsers();

  const upsert = useUpsertTeam();
  const archive = useArchiveTeam();

  const [editing, setEditing] = useState<Team | null>(null);
  const [open, setOpen] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [leaderId, setLeaderId] = useState('');
  const [areas, setAreas] = useState('');

  // Only team leaders and managers make sense as a team's leader.
  const leaderOptions = (users ?? [])
    .filter((user) => ['team_leader', 'sales_manager', 'admin'].includes(user.role))
    .map((user) => ({ value: user.id, label: user.full_name }));

  const openFor = (team: Team | null) => {
    setEditing(team);
    setName(team?.name ?? '');
    setLeaderId(team?.leader_id ?? '');
    setAreas((team?.areas ?? []).join('، '));
    setFailure(null);
    setOpen(true);
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFailure(null);
    try {
      await upsert.mutateAsync({
        id: editing?.id ?? null,
        name,
        leader_id: leaderId || null,
        // Accept both the Arabic comma and the Latin one — staff will type either.
        areas: areas
          .split(/[،,]/)
          .map((area) => area.trim())
          .filter(Boolean),
      });
      setOpen(false);
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t('common.error'));
    }
  };

  const onArchive = (team: Team) => {
    const reason = window.prompt(t('admin.teams.archiveReason'));
    // #30: archiving must be explainable. The database rejects a blank reason too.
    if (!reason?.trim()) return;

    setFailure(null);
    archive.mutateAsync({ id: team.id, reason: reason.trim() }).catch((cause: unknown) => {
      setFailure(cause instanceof Error ? cause.message : t('common.error'));
    });
  };

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold text-slate-900">{t('nav.teams')}</h1>
        <Button onClick={() => openFor(null)}>{t('admin.teams.create')}</Button>
      </div>

      {failure ? <Alert tone="error">{failure}</Alert> : null}
      {isError ? <Alert tone="error">{t('common.error')}</Alert> : null}

      {isPending ? (
        <Spinner />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-start">
                  {t('admin.teams.name')}
                </th>
                <th scope="col" className="px-3 py-2 text-start">
                  {t('admin.teams.leader')}
                </th>
                <th scope="col" className="px-3 py-2 text-start">
                  {t('admin.teams.areas')}
                </th>
                <th scope="col" className="px-3 py-2 text-start">
                  {t('admin.teams.actions')}
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {(teams ?? []).map((team) => (
                <tr key={team.id}>
                  <td className="px-3 py-2 font-medium text-slate-900">{team.name}</td>
                  <td className="px-3 py-2 text-slate-600">
                    {(users ?? []).find((u) => u.id === team.leader_id)?.full_name ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{team.areas.join('، ') || '—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2">
                      <Button variant="secondary" onClick={() => openFor(team)}>
                        {t('admin.teams.edit')}
                      </Button>
                      <Button variant="ghost" onClick={() => onArchive(team)}>
                        {t('admin.teams.archive')}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={open}
        title={editing ? t('admin.teams.edit') : t('admin.teams.create')}
        onClose={() => setOpen(false)}
      >
        <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4">
          <Input
            label={t('admin.teams.name')}
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <Select
            label={t('admin.teams.leader')}
            placeholder={t('admin.teams.noLeader')}
            value={leaderId}
            onChange={(e) => setLeaderId(e.target.value)}
            options={leaderOptions}
          />

          <Input
            label={t('admin.teams.areas')}
            hint={t('admin.teams.areasHint')}
            value={areas}
            onChange={(e) => setAreas(e.target.value)}
          />

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={upsert.isPending}>
              {t('common.save')}
            </Button>
          </div>
        </form>
      </Modal>
    </section>
  );
}
